import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import WorldIcon from './WorldIcon';

const countries: Record<string, string> = {
  es: 'https://www.kidsandus.es/es/',
  en: 'https://www.kidsandus.es/en-es/',
  it: 'https://www.kidsandus.it/it',
  fr: 'https://www.kidsandus.fr/fr',
  be: 'https://www.kidsandus.be/fr-be',
  mx: 'https://www.kidsandus.mx/es-mx',
  cz: 'https://www.kidsandus.cz/cs/',
  sk: 'https://www.kidsandus.sk/sk/',
  jp: 'https://kidsandusjapan.com/',
  vi: 'https://kidsandus.net.vn/',
  my: 'https://kidsandus.org.mm/',
};

const translations: Record<string, string> = {
  ca: 'ES-CA',
  eu: 'ES-EU',
  gl: 'ES-GL',
  it: 'IT',
  fr: 'FR',
  pl: 'PL',
  nl: 'NL',
  es: 'ES',
  en: 'ES-EN',
  cz: 'CZ',
  sk: 'SK',
  vi: 'VI',
  jp: 'JP',
  my: 'MY'
};

const detectLanguage = (fallbackLang = 'es'): string => {
  const segments = window.location.pathname.split('/').filter(Boolean);

  for (const segment of segments) {
    if (translations[segment]) return segment;
  }

  const browserLang = navigator.language.split('-')[0];
  if (translations[browserLang]) return browserLang;

  return fallbackLang;
};

const FloatingMenu = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0);
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-block;
  margin-right: 1rem;
`;

const CountryList = styled.ul<{ visible: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  position: absolute;
  bottom: 100%;
  left: 0;
  background: rgba(0, 0, 0);
  padding: 10px;
  border-radius: 6px;
  z-index: 10;
`;

const CountryItem = styled.li`
  margin: 5px 0;
`;

const CountryLink = styled.a`
  color: white;
  text-decoration: none;
  padding: 5px 10px;
  display: block;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const SelectedCountry = styled.div`
  color: white;
  text-align: center;
`;

interface FloatingCountryMenuProps {
  lang?: string;
}

const FloatingCountryMenu: React.FC<FloatingCountryMenuProps> = ({ lang }) => {
  const [visible, setVisible] = useState(false);
  const [detectedLang, setDetectedLang] = useState('es');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const finalLang = lang || detectLanguage(lang);
    setDetectedLang(finalLang);
  }, [lang]);

  const label = useMemo(() => translations[detectedLang] || 'ES', [detectedLang]);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(prev => !prev);
  };

  const handleRedirect = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = url;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <FloatingMenu onClick={toggleMenu} ref={menuRef}>
<SelectedCountry>
  <WorldIcon width={16} height={16} color="#fff" style={{ marginRight: '6px', verticalAlign: 'middle' }} />
  {label}
</SelectedCountry>
      <CountryList visible={visible}>
        {Object.entries(countries).map(([code, url]) => (
          <CountryItem key={code}>
            <CountryLink href={url} onClick={(e) => handleRedirect(url, e)}>
              {code.toUpperCase()}
            </CountryLink>
          </CountryItem>
        ))}
      </CountryList>
    </FloatingMenu>
  );
};

export default FloatingCountryMenu;
